import React, { useState } from "react";
import AccordionTable from "../../../../components/accordion/AccordionTable";
import { Box, Grid, MenuItem, Tooltip, Typography } from "@mui/material";

import { InputValueProjectForm } from "./domain";
import { ButtonPrimary } from "../../../../components/button/ButtonCustoms";
import ModalCustom from "../../../../components/modal/ModalCustom";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routeName } from "../../../../routes/routeName";
import InputTextField from "../../../../components/input/inputTextField";
import FilterSelect from "../../../../components/select/filterSelect";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../../components/alert/swal";
import { UNIT_MEASUREMENT_OF_INDICATORS } from "../enum";
import { edpexProjectAPI } from "../../../../api/edpex-project/edpex-project.api";
import StatusCustom from "../../../../components/status/statusCustom";

interface IProjectFormProps {
  inputValue: InputValueProjectForm;
  setInputValue: React.Dispatch<React.SetStateAction<InputValueProjectForm>>;
  disabled?: boolean;
  type?: string;
}

const IndicatorFormResult: React.FC<IProjectFormProps> = ({
  inputValue,
  setInputValue,
  disabled = false,
  type,
}) => {
  const navigate: NavigateFunction = useNavigate();
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState<boolean>(false);

  const [inputValueIndicator, setInputValueIndicator] = useState<any>({
    indicator_id: "",
    indicator_name: "",
    indicator_type: "",
    indicator_type_name: "",
    indicator_value_1: "",
    indicator_value_2: "",
  });

  const [mode, setMode] = useState<string>("ADD");

  const fileHeaders: any[] = [
    {
      key: "index",
      label: "ลำดับ",
      width: 1,
      align: "center" as const,
      render: (value: any, rowData: any, rowIndex: number) => (
        <Typography variant="body2">
          {/* {JSON.stringify(rowData)} */}
          {rowIndex + 1}
        </Typography>
      ),
    },
    {
      key: "name",
      label: "ชื่อตัวชี้วัด",
      width: 3,
      render: (value: string, rowData: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={value}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      key: "value",
      label: "ค่าเป้าหมาย",
      width: type === "COMPLETED" ? 2 : 3,
      align: "center" as const,
    },

    {
      key: "value_3",
      label: "ผลลัพธ์ค่าเป้าหมาย",
      width: type === "COMPLETED" ? 2 : 3,
      render: (value: string, rowData: any, rowIndex: number) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {type !== "COMPLETED" ? (
            <InputTextField
              value={rowData.value_3 || ""}
              onchange={(e: any) => {
                // Update the specific row's result value
                const updatedList = [...inputValue.indicatorList];
                updatedList[rowIndex] = {
                  ...updatedList[rowIndex],
                  value_3: e.target.value,
                };
                setInputValue((prev) => ({
                  ...prev,
                  indicatorList: updatedList,
                }));
              }}
              required
              helperText={""}
              disabled={disabled}
            />
          ) : (
            rowData?.value_3
          )}
        </Box>
      ),
    },
    type === "COMPLETED" && {
      key: "result",
      label: "ผลลัพธ์",
      width: 2,
      align: "center" as const,
      render: (value: string, rowData: any, rowIndex: number) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* {rowData.value_1}
          {JSON.stringify(rowData)} */}
          {+rowData.value_3 >= +rowData.value ? (
            <StatusCustom disableMaxWidth text={"ผ่านเกณฑ์"} type={"approve"} />
          ) : (
            <StatusCustom
              disableMaxWidth
              text={"ไม่ผ่านเกณฑ์"}
              type={"REJECT"}
            />
          )}
        </Box>
      ),
    },
  ].filter((h): h is any => h !== null);

  const handleInputChange = (key: any, value: any) => {
    setInputValueIndicator((prev: any) => ({ ...prev, [key]: value }));
  };

  const indicatorTypeOption = [
    {
      name: "หน่วยวัดระดับ",
      value: UNIT_MEASUREMENT_OF_INDICATORS.LEVEL,
    },
    {
      name: "หน่วยวัดจำนวน",
      value: UNIT_MEASUREMENT_OF_INDICATORS.QUANTITY,
    },
    {
      name: "หน่วยวัดคะแนน",
      value: UNIT_MEASUREMENT_OF_INDICATORS.SCORE,
    },
    {
      name: "หน่วยวัดเปอร์เซ็นต์",
      value: UNIT_MEASUREMENT_OF_INDICATORS.PERCENT,
    },
    {
      name: "หน่วยวัดร้อยละ",
      value: UNIT_MEASUREMENT_OF_INDICATORS.RATIO_PERCENT,
    },
    {
      name: "หน่วยวัดสัดส่วน",
      value: UNIT_MEASUREMENT_OF_INDICATORS.PROPORTION,
    },
  ];

  const handleFileUpload = async (files: File[]): Promise<number[]> => {
    if (files.length === 0) return [];

    try {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append("files", file);
      });

      const res = await edpexProjectAPI.uploadEdpexProjectFile(formData);

      // Extract file IDs from the correct path: res.data.data
      const fileIds =
        res.data?.data?.map((file: any) => file.id_edpex_project_file) || [];
      return fileIds;
    } catch (error: any) {
      console.error("File upload error:", error);
      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || t("อัปโหลดไฟล์ไม่สำเร็จ")
      );
      return [];
    }
  };

  return (
    <>
      {showModal && (
        <ModalCustom
          open
          title={"เพิ่มตัวชี้วัด"}
          subTitle={t("")}
          onClose={() => {
            setShowModal(false);
          }}
          component={
            <div className="w-full">
              <section className="w-full mt-10">
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <InputTextField
                      heading={"ชื่อตัวชี้วัด"}
                      placeholder={"ชื่อตัวชี้วัด"}
                      value={inputValueIndicator.indicator_name ?? ""}
                      onchange={(e: any) =>
                        handleInputChange("indicator_name", e.target.value)
                      }
                      required
                      helperText={""}
                      disabled={false}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <FilterSelect
                      value={inputValueIndicator.indicator_type ?? ""}
                      labelId="ประเภทค่าเป้าหมาย-id"
                      selectId="ประเภทค่าเป้าหมาย-select-id"
                      heading="ประเภทค่าเป้าหมาย"
                      options={[...indicatorTypeOption].map(
                        (item: any, index: number) => (
                          <MenuItem value={item?.value} key={index + 1}>
                            {item?.name}
                          </MenuItem>
                        )
                      )}
                      onchange={(e: any) => {
                        const selectedValue = e.target.value;
                        const selectedItem = indicatorTypeOption.find(
                          (item) => item?.value === selectedValue
                        );

                        handleInputChange("indicator_type", selectedValue);
                        handleInputChange(
                          "indicator_type_name",
                          selectedItem?.name || ""
                        );
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={12}
                    lg={12}
                    sx={{
                      alignSelf: "end",
                    }}
                  >
                    <Typography variant="body1" gutterBottom>
                      ระบุค่าเป้าหมาย
                    </Typography>
                  </Grid>

                  {inputValueIndicator.indicator_type && (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={3}
                        lg={3}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <Typography variant="body1" gutterBottom>
                          {inputValueIndicator.indicator_type_name}
                        </Typography>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        lg={3}
                        sx={{ alignSelf: "end" }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <InputTextField
                            placeholder="ระบุ"
                            value={inputValueIndicator.indicator_value_1 ?? ""}
                            onchange={(e: any) =>
                              handleInputChange(
                                "indicator_value_1",
                                e.target.value
                              )
                            }
                            required
                            helperText=""
                            disabled={disabled}
                          />

                          {inputValueIndicator.indicator_type ===
                            UNIT_MEASUREMENT_OF_INDICATORS.PROPORTION && (
                            <>
                              <Typography variant="body1">/</Typography>
                              <InputTextField
                                placeholder="ระบุ"
                                value={
                                  inputValueIndicator.indicator_value_2 ?? ""
                                }
                                onchange={(e: any) =>
                                  handleInputChange(
                                    "indicator_value_2",
                                    e.target.value
                                  )
                                }
                                required
                                helperText=""
                                disabled={false}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        lg={3}
                        sx={{
                          alignSelf: "end",
                        }}
                      >
                        <Typography variant="body1" gutterBottom>
                          {inputValueIndicator.indicator_type ===
                          UNIT_MEASUREMENT_OF_INDICATORS.SCORE
                            ? "คะแนน"
                            : inputValueIndicator.indicator_type ===
                              UNIT_MEASUREMENT_OF_INDICATORS.PERCENT
                            ? "%"
                            : ""}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </section>

              <div className="mt-6 w-full flex justify-center gap-4 items-center">
                <span>
                  <ButtonPrimary
                    mode="cancel"
                    buttonName={t("BUTTON.CANCEL")}
                    onClick={() => {
                      setShowModal(false);
                    }}
                  />

                  <ButtonPrimary
                    mode="next"
                    buttonName={t("ยืนยัน")}
                    onClick={() => {}}
                  />
                </span>
              </div>
            </div>
          }
        />
      )}

      <AccordionTable
        defaultExpanded
        required
        titleName="กรุณาระบุผลลัพธ์ค่าเป้าหมาย"
        headers={fileHeaders}
        data={inputValue.indicatorList}
        enableButtonLeft
        isView={true}
      />
    </>
  );
};

export default IndicatorFormResult;
